// Shared construction of a bundle member's cart-line properties.
//
// Extracted 2026-07-02 from onAddToCart's inline closure so the SAME logic
// drives both add paths:
//   1. the in-flow bundle add (member ticked before add-to-cart), and
//   2. the post-ATC "complete the set" one-tap card (SecondItemCard).
// Duplicating this on a money path risked drift: these properties decide
// what the embroidery team stitches on the member garment.
//
// Pure functions, no React, no side effects.

import type { BundleMember, Config } from "../types";

/** The subset of the primary garment's design properties that propagate to
 *  bundle members: everything except custom fields explicitly marked
 *  propagateAcrossBundle === false. */
export function buildPropagatedProps(
  props: Record<string, string>,
  customFields: Config["customFields"],
): Record<string, string> {
  const cfNonPropagateKeys = new Set<string>();
  for (const f of customFields ?? []) {
    if (f.propagateAcrossBundle === false) {
      const key = f.cartPropertyVisible
        ? f.cartPropertyKey
        : `_${f.cartPropertyKey.replace(/^_+/, "")}`;
      cfNonPropagateKeys.add(key);
    }
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(props)) {
    if (cfNonPropagateKeys.has(k)) continue;
    out[k] = v;
  }
  return out;
}

/** Cart-line properties for one bundle member. `propagatedProps` comes from
 *  buildPropagatedProps (or a stash of it taken at add-to-cart time).
 *
 *  - propagateDesign members inherit the design keys, with the member's
 *    per-product placement/size defaults overriding the primary's
 *    "Spot N PLACEMENT" / "Spot N SIZE" values.
 *  - plain members carry only the marker keys.
 *  - `_rk_price_delta_cents` never propagates (it describes the whole
 *    group, not the member). */
export function buildMemberCartProps(
  member: Pick<
    BundleMember,
    "id" | "propagateDesign" | "bundleDefaultPlacementLabel" | "bundleDefaultSizeMm"
  >,
  groupId: string,
  propagatedProps: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {
    _added_by: member.propagateDesign
      ? "rk-builder-bundle"
      : "rk-builder-bundle-plain",
    _rk_group_id: groupId,
    _rk_bundle_member_id: member.id,
  };
  if (member.propagateDesign) {
    for (const [k, v] of Object.entries(propagatedProps)) {
      if (k === "_added_by" || k === "_rk_group_id") continue;
      if (k === "_rk_price_delta_cents") continue;
      out[k] = v;
    }
    if (member.bundleDefaultPlacementLabel) {
      for (const k of Object.keys(out)) {
        if (/^Spot \d+ PLACEMENT$/.test(k)) {
          out[k] = member.bundleDefaultPlacementLabel;
        }
      }
    }
    if (
      typeof member.bundleDefaultSizeMm === "number" &&
      member.bundleDefaultSizeMm > 0
    ) {
      for (const k of Object.keys(out)) {
        if (/^Spot \d+ SIZE$/.test(k)) {
          out[k] = `${member.bundleDefaultSizeMm}mm`;
        }
      }
    }
  }
  return out;
}
